import React, { useCallback, useEffect, useState } from 'react';
import axios, { isAxiosError } from 'axios';
import axiosInstance from '../../../axios';
import { isRecord, type TrainingApiResponse } from '../types';
import { parseTrainingDisplay } from '../lib/parseTrainingDisplay';
import TrainingFullStudentView from './TrainingFullStudentView';
import './TrainingProgressBlock.scss';

type Props = {
  /** Học viên: gọi API không kèm cccd (server dùng CCCD trong hồ sơ). Staff: bắt buộc truyền cccd. */
  mode: 'student' | 'staff';
  cccd?: string | null;
  /** Ẩn breadcrumb khi nhúng trong drawer / card portal. */
  compact?: boolean;
  className?: string;
};

const TrainingProgressBlock: React.FC<Props> = ({
  mode,
  cccd,
  compact = false,
  className,
}) => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<TrainingApiResponse | null>(null);
  const [httpError, setHttpError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const trimmedCccd = cccd?.trim() ?? '';
  const skipForMissingCccd = mode === 'staff' && !trimmedCccd;

  const load = useCallback(async (force = false) => {
    if (skipForMissingCccd) {
      setLoading(false);
      setPayload(null);
      setHttpError(null);
      return;
    }
    if (!force) setLoading(true);
    setHttpError(null);
    try {
      const params: Record<string, string> = {};
      if (mode === 'staff' && trimmedCccd) params.cccd = trimmedCccd;
      if (force) params.force = '1';
      const res = await axiosInstance.get<TrainingApiResponse>('/api/training/student-cached', { params });
      setPayload(res.data);
    } catch (e: unknown) {
      setPayload(null);
      const msg =
        isAxiosError(e) && e.response?.data && isRecord(e.response.data as unknown)
          ? String((e.response.data as Record<string, unknown>).EM ?? 'Lỗi tải dữ liệu')
          : 'Lỗi tải dữ liệu';
      setHttpError(msg);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [mode, skipForMissingCccd, trimmedCccd]);

  const handleForceSync = useCallback(() => {
    setSyncing(true);
    void load(true);
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const rootClass = ['tpb', className].filter(Boolean).join(' ');

  if (skipForMissingCccd) {
    return (
      <div className={rootClass}>
        <p className="tpb__muted">Chưa có số CCCD trên hồ sơ — không thể tra cứu tiến độ hệ đào tạo.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={rootClass}>
        <div className="tpb__loading">
          <span className="material-icons tpb__spin">sync</span>
          <span>Đang tải tiến độ đào tạo...</span>
        </div>
      </div>
    );
  }

  if (httpError) {
    return (
      <div className={rootClass}>
        <p className="tpb__error">{httpError}</p>
        <button type="button" className="tpb__retry" onClick={() => void load()}>
          Thử lại
        </button>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className={rootClass}>
        <p className="tpb__muted">Không có dữ liệu.</p>
      </div>
    );
  }

  if (payload.EC !== 0) {
    return (
      <div className={rootClass}>
        <p className="tpb__error">{payload.EM || 'Không lấy được dữ liệu'}</p>
        <button type="button" className="tpb__retry" onClick={() => void load()}>
          Thử lại
        </button>
      </div>
    );
  }

  const dt = payload.DT;
  if (!isRecord(dt)) {
    return (
      <div className={rootClass}>
        <p className="tpb__muted">Dữ liệu không đúng định dạng.</p>
      </div>
    );
  }

  const display = parseTrainingDisplay(dt);

  return (
    <div className={rootClass}>
      <div className="tpb__sync-bar">
        <button
          type="button"
          className="tpb__sync-btn"
          onClick={handleForceSync}
          disabled={syncing}
        >
          <span className={`material-icons${syncing ? ' tpb__spin' : ''}`}>
            {syncing ? 'sync' : 'cloud_download'}
          </span>
          {syncing ? 'Đang đồng bộ...' : 'Đồng bộ dữ liệu'}
        </button>
        {(payload as Record<string, unknown>)?._sync ? (
          <span className="tpb__sync-time">
            Cập nhật: {new Date(String(((payload as Record<string, unknown>)._sync as Record<string, unknown>).lastSyncAt)).toLocaleString('vi-VN')}
          </span>
        ) : null}
      </div>
      <TrainingFullStudentView display={display} showBreadcrumbs={!compact} />
    </div>
  );
};

export default TrainingProgressBlock;
